import React, { useState, useEffect, useContext } from "react";
import { Route } from "react-router-dom";
import styled from "styled-components";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import ReactGA from "react-ga4";
import i18next from "i18next";

// Import your existing components and utilities
import Context from "./Context";
import Logo from "./Images/Logonew.jpeg";
import { authGoogle } from "./views/utilities/Authentication";
import { statusFailure } from "./views/Constant";
import { CustomLanguageDropdown } from "./Componets/CustomLanguageDropdown";

const SecuredRoute = ({ component: Component, ...rest }) => {
  const { t } = useTranslation();
  const { dispatch } = useContext(Context);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clientId = "451080537233-a0gdg7j86p0valvd0vbs161ebeqgq0lt.apps.googleusercontent.com";

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("loginDetails");
      const authenticated = !!token;
      
      setIsAuthenticated(authenticated);
      setShowLoginPopup(!authenticated);
      setIsLoading(false);
    };

    // Initial check
    checkAuth();

    // Listen for storage changes (handles logout from other tabs)
    const handleStorageChange = () => {
      checkAuth();
    };

    // Listen for custom logout events
    const handleLogout = () => {
      checkAuth();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("logout", handleLogout);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("logout", handleLogout);
    };
  }, []);

  // Handle language switching
  const switchLng = (lng) => {
    i18next.changeLanguage(lng);
  };

  const handlelng = (event) => {
    const selectedLanguage = event.target.value;
    switchLng(selectedLanguage);
  };

  // Handle Google login response
  const responseGoogle = (credentialResponse) => {
    if (credentialResponse.credential) {
      // console.log("Google login response:", credentialResponse);
      let tokenId = credentialResponse.credential;

      authGoogle({ token: tokenId })
        .then((response) => {
          // console.log("Google Check", response);

          if (response.status !== statusFailure) {
            // Store the token properly
            localStorage.setItem("loginDetails", response.data.token);
            dispatch({ type: "login", payload: response.data.token });

            // Track the event
            if (response.message === "Account created successfully.") {
              ReactGA.event({
                category: "User",
                action: "Register Success",
                label: "Google Register Success",
              });
              // console.log("Account created successfully");
            } else if (response.message === "User already exist") {
              ReactGA.event({
                category: "User",
                action: "Login Success",
                label: "Google Login Success",
              });
              // console.log("Login successful");
            }

            // Update authentication status
            setIsAuthenticated(true);
            setShowLoginPopup(false);

            // Reload the page after successful login
            window.location.reload();
          }
        })
        .catch((error) => {
          console.error("Google auth error:", error);
          localStorage.removeItem("loginDetails");
        });
    }
  };

  // Handle popup close
  const handleClosePopup = () => {
    setShowLoginPopup(false);
    // Optionally redirect to home or show a message
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isLoading) {
          return (
            <LoadingContainer>
              <div>Loading...</div>
            </LoadingContainer>
          );
        }

        return (
          <>
            {/* Render the component regardless of auth status */}
            <Component {...props} />
            
            {/* Show login popup if not authenticated */}
            {showLoginPopup && (
              <PopupOverlay>
                <PopupContent>
                  <CloseButton onClick={handleClosePopup}>
                    <Plus className="cross" />
                  </CloseButton>
                  
                  <img
                    className="img"
                    height="80px"
                    width="80px"
                    src={Logo}
                    alt="logo"
                  />
                  
                  <center>
                    <p className="event-p3">{t("Login.Welcome")}</p>
                  </center>
                  
                  <div className="login-container">
                    <GoogleOAuthProvider clientId={clientId}>
                      <GoogleLogin
                        onSuccess={responseGoogle}
                        onError={() => {
                          console.error("Google Login Failed");
                        }}
                        useOneTap={false}
                        ux_mode="popup"
                        type="standard"
                        theme="filled_black"
                        text="continue_with"
                        shape="rectangular"
                        locale="en"
                      />
                    </GoogleOAuthProvider>
                  </div>
                  
                  <CustomLanguageDropdown onChange={handlelng} />

                  <div style={{ textAlign: "center", marginTop: "30px" }}>
                    <p style={{ color: "white" }}>
                      By continuing, you agree to our <br />
                      <Link
                        to="/term-condition"
                        className="text-blue underline mx-1"
                      >
                        terms and conditions
                      </Link>
                      and
                      <Link
                        to="/privacy-policy"
                        className="text-blue underline mx-1"
                      >
                        privacy policy
                      </Link>
                      .
                    </p>
                  </div>
                </PopupContent>
              </PopupOverlay>
            )}
          </>
        );
      }}
    />
  );
};

export default SecuredRoute;

// Styled components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  color: white;
  font-size: 18px;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background-color: #0f1817;
  padding: 20px;
  border-radius: 14px;
  width: 90%;
  height: auto;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  h2 {
    color: white;
    text-align: center;
    margin-bottom: 20px;
    font-size: 24px;
  }

  .login-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .event-p3 {
    color: white;
    font-size: 18px;
    margin-bottom: 20px;
  }

  .img {
    border-radius: 50%;
    margin-bottom: 15px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  
  .cross {
    transform: rotate(45deg);
    color: white;
    width: 20px;
    height: 20px;
  }

  &:hover .cross {
    color: #ccc;
  }
`;